import { type FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useCheckRequirementItem } from '@common/hooks/check-requirements';
import { getImage } from '@common/lib/images';
import { getText } from '@common/lib/text';
import { type RequirementsItemProps } from '@common/types/components';
import { type UnionGameItemType } from '@common/types/userType';
import { StringSmall } from '@components/Typography/Typography';
import { Container } from '@components/ui/Container/container';
import { Picture } from '@components/ui/Picture/Picture';
import { selectBuildLevelByName } from '@redux/selectors/userSelectors';
import { useAppSelector } from '@redux/store';
import cn from 'classnames';
import { useInterval } from 'usehooks-ts';

import { toDifferenceFormat } from '@/common/lib/toDifferenceFormat';

import { createRequirementLink } from './requirement-link';

import checkIcon from '/img/icons/check.svg';
import chevronIcon from '/img/icons/chevron-right-gold.svg';

const RequirementsItem: FC<RequirementsItemProps> = ({
  type,
  resourceName,
  value,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isComplete, currentValue } = useCheckRequirementItem({
    type,
    resourceName,
    value,
  });
  const [activeChevronIndex, setActiveChevronIndex] = useState(0);
  const plantLevel = useAppSelector((state) =>
    selectBuildLevelByName(state, resourceName as UnionGameItemType),
  );

  useInterval(() => {
    setActiveChevronIndex((state) => (state === 2 ? 0 : state + 1));
  }, 500);

  const image = getImage({
    name: resourceName || type,
    field:
      type === 'cash' || type === 'tgem'
        ? 'default'
        : type === 'plant' || type === 'quarry'
          ? 'build'
          : 'icon',
  });
  const text = getText({
    name: resourceName || type,
    kind: type === 'plant' || type === 'quarry' ? 'build' : 'resource',
    t,
  });

  const doRequirement = (): void => {
    const link = createRequirementLink({
      value,
      type,
      name: resourceName,
      plantLevel: plantLevel || 0,
    });

    if (link) {
      navigate(link, { state: { resourceName } });
    }
  };

  return (
    <Container
      className={cn(
        'w-full flex items-center justify-between',
        !isComplete && 'cursor-pointer',
      )}
      padding="4-8"
      variant="task-blur"
      onClick={!isComplete ? doRequirement : undefined}
    >
      <div className="flex items-center gap-2">
        {image && (
          <Picture
            className="w-[2rem] h-[2rem]"
            img={image.img}
            webp={image.webp}
            alt={resourceName || type}
          />
        )}
        <StringSmall weight="medium" className="text-white capitalize">
          {text}
        </StringSmall>
      </div>
      <div className="flex items-center gap-4">
        <StringSmall
          weight="medium"
          className={cn('flex items-center', !isComplete && 'gap-[2px]')}
        >
          {type === 'quarry' || type === 'plant' ? (
            <span
              className={isComplete ? 'text-white' : 'text-red'}
            >{`${t('Level')} ${value}`}</span>
          ) : (
            <>
              <span className={isComplete ? 'text-white/40' : 'text-red'}>
                {toDifferenceFormat({
                  value: currentValue,
                  diminutive: currentValue,
                  subtractive: value || 0,
                })}
              </span>
              <span>/</span>
              <span>
                {toDifferenceFormat({
                  value: value || 0,
                  diminutive: currentValue,
                  subtractive: value || 0,
                })}
              </span>
            </>
          )}
        </StringSmall>
        {isComplete ? (
          <img className="w-6 h-6" src={checkIcon} alt="check" />
        ) : (
          <div className="flex items-center">
            {Array.from({ length: 3 }).map((_, index) => (
              <img
                key={index}
                className={cn(
                  'w-[0.46875rem] h-[0.84375rem] transition-all',
                  !(activeChevronIndex === index) && 'opacity-30',
                )}
                src={chevronIcon}
                alt="chevron"
              />
            ))}
          </div>
        )}
      </div>
    </Container>
  );
};

export { RequirementsItem };
